import { useState } from 'react';
import { IonItem, IonLabel, IonList, IonRadio, IonRadioGroup } from '@ionic/react';
import axios from 'axios';
import { useStoreState } from 'pullstate';
import StepLayout, { STEP_TITLE_ID } from './StepLayout';
import AddressPicker, { composeManualAddress, isManualAddressComplete } from './AddressPicker';
import { isInRestrictedArea } from '../../../helpers/restrictedArea';
import {
    addressFormStore,
    appStore,
    coordsStore,
    modalToggleStore,
    permitsStore,
    profileFormStore,
    toggleStore,
} from '../../../store/Store';

const LOOKUP_ERROR =
    "We couldn't find that address. Please check it and try again, or contact the Parry Sound Fire Department at 705-746-2262.";

const ApplyPermit4 = () => {
    const polygonCoords = coordsStore.useState((s) => s.polygonCoords);
    const circleCoords = coordsStore.useState((s) => s.circleCoords);
    const rectangleCoords = coordsStore.useState((s) => s.rectangleCoords);
    const addressForm = addressFormStore.useState((s) => s.addressForm);
    const chooseAddressToggle = toggleStore.useState((s) => s.chooseAddressToggle);
    const userInfo = appStore.useState((s) => s.userInfo);
    const addressPass = appStore.useState((s) => s.addressPass);
    const { mapsAPI } = useStoreState(profileFormStore);
    const [checking, setChecking] = useState(false);
    const [lookupError, setLookupError] = useState('');

    const useOwnAddress = chooseAddressToggle !== 'manual';
    const addressReady = useOwnAddress
        ? Boolean(userInfo.address)
        : isManualAddressComplete(addressForm);

    const chooseAddress = (value) => {
        toggleStore.update((s) => {
            s.chooseAddressToggle = value;
        });
        appStore.update((s) => {
            s.addressPass = true;
        });
        setLookupError('');
    };

    const openFifthPage = () => {
        modalToggleStore.update((s) => {
            s.permitPage = 5;
        });
    };

    const validateAddress = async () => {
        const address = useOwnAddress ? userInfo.address : composeManualAddress(addressForm);
        permitsStore.update((s) => {
            s.currentPermitAddress = address;
        });
        appStore.update((s) => {
            s.addressPass = true;
        });
        setLookupError('');
        setChecking(true);

        let advanced = false;
        try {
            const response = await axios.get('https://maps.googleapis.com/maps/api/geocode/json', {
                params: { address, key: mapsAPI },
            });
            const match = response.data.results && response.data.results[0];
            if (!match) {
                throw new Error('Address not found');
            }
            const { lat, lng } = match.geometry.location;
            coordsStore.update((s) => {
                s.currentBurnCoord = { lat, lng };
            });
            const restricted = isInRestrictedArea(lat, lng, {
                circleCoords,
                rectangleCoords,
                polygonCoords,
            });
            appStore.update((s) => {
                s.addressPass = !restricted;
            });
            if (!restricted) {
                advanced = true;
                openFifthPage();
            }
        } catch (error) {
            setLookupError(LOOKUP_ERROR);
        } finally {
            if (!advanced) {
                setChecking(false);
            }
        }
    };

    let hint;
    if (checking) {
        hint = 'Checking your address…';
    } else if (!addressReady) {
        hint = useOwnAddress
            ? 'There is no address on your profile. Choose "Use another address".'
            : 'Enter or search for a complete address to continue.';
    }

    return (
        <StepLayout
            title='Choose address'
            primaryLabel={checking ? 'Checking…' : 'Continue'}
            onPrimary={validateAddress}
            primaryDisabled={!addressReady || checking}
            hint={hint}
        >
            <IonRadioGroup
                value={chooseAddressToggle}
                onIonChange={(e) => chooseAddress(e.detail.value)}
                aria-labelledby={STEP_TITLE_ID}
            >
                <IonList lines='full'>
                    <IonItem>
                        <IonRadio slot='start' color='tertiary' value='auto' />
                        <IonLabel className='ion-text-wrap'>
                            <strong>Use my address</strong>
                            <span className='flow-choice__detail'>
                                {userInfo.address || 'No address on file'}
                            </span>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonRadio slot='start' color='tertiary' value='manual' />
                        <IonLabel className='ion-text-wrap'>Use another address</IonLabel>
                    </IonItem>
                </IonList>
            </IonRadioGroup>
            {!useOwnAddress && <AddressPicker idPrefix='permit-address' />}
            {!addressPass && (
                <p className='flow-step__error' role='alert'>
                    The address you entered falls in the restricted area.
                </p>
            )}
            {lookupError && (
                <p className='flow-step__error' role='alert'>
                    {lookupError}
                </p>
            )}
        </StepLayout>
    );
};

export default ApplyPermit4;
